import _ from 'lodash'
import { translateString } from 'shared-libs/helpers/utils'

export const DEFAULT_ENTITY_TYPE_NAME = 'document'

export const DEFAULT_SCHEMA_PATHS = {
  entitySearchResultCard: 'uiSchema.shared.entitySearchResultCardSchema',
  searchHelp: 'uiSchema.shared.searchHelpSchema',
  entityPreviewCard: 'uiSchema.shared.entityPreviewCardSchema',
  searchEmptyResults: 'uiSchema.shared.searchEmptyResultsSchema',
  entityCreate: 'uiSchema.shared.entityCreateSchema',
  entityEdit: 'uiSchema.shared.entityEditSchema',
  entityReadOnly: 'uiSchema.shared.entityReadOnlySchema',
}

export interface ResolvedEntityDisplayNames {
  singular: string
  plural: string
}

/**
 * Resolves the singular + plural display names for the target entity type.
 *
 * Resolution order (first non-empty wins):
 *   query.entityDisplayName → query.itemName → schema.metadata.entityTypeDisplayName
 *   → probed `entityDisplayName` translation key → 'document'
 *
 * The resolved key is translated via the merged table (form + entity schema).
 * `singular` uses count:1, `plural` uses count:2 (hits the `many` branch).
 */
export function resolveEntityDisplayNames(
  query: any,
  entitySchema: any,
  formTranslationTable: any,
): ResolvedEntityDisplayNames {
  const mergedTable = _.merge({}, formTranslationTable, entitySchema?.translations)

  let key: string | undefined =
    query?.entityDisplayName ??
    query?.itemName ??
    entitySchema?.metadata?.entityTypeDisplayName

  if (!key) {
    const probed = translateString('entityDisplayName', mergedTable, { count: 1 })
    key = probed !== 'entityDisplayName' ? 'entityDisplayName' : DEFAULT_ENTITY_TYPE_NAME
  }

  return {
    singular: translateString(key, mergedTable, { count: 1 }),
    // count > 1 (integer) hits the `many` branch; string 'many' would not
    plural: translateString(key, mergedTable, { count: 2 }),
  }
}

export interface ResolvedSchemaPaths {
  entitySearchResultCard?: string
  searchHelp?: string
  searchEmptyResults?: string
  entityPreviewCard?: string
  entityCreate?: string
}

/** Validates each schema path prop against the entity schema, returning only paths that resolve. */
export function resolveSchemaPaths(
  props: {
    isCreatable?: boolean
    entitySearchResultCardSchemaPath?: string
    searchHelpSchemaPath?: string
    searchEmptyResultsSchemaPath?: string
    entityPreviewCardSchemaPath?: string
    entityCreateSchemaPath?: string
  },
  entitySchema: any,
): ResolvedSchemaPaths {
  const get = (path?: string) =>
    path && _.get(entitySchema, path) ? path : undefined

  return {
    entitySearchResultCard: get(props.entitySearchResultCardSchemaPath),
    searchHelp: get(props.searchHelpSchemaPath),
    searchEmptyResults: get(props.searchEmptyResultsSchemaPath),
    entityPreviewCard: get(props.entityPreviewCardSchemaPath),
    entityCreate: props.isCreatable ? get(props.entityCreateSchemaPath) : undefined,
  }
}

/** True when `schema` is a top-level entityPreviewCard component schema. */
export function isEntityPreviewCardSchema(schema: any): boolean {
  return (
    schema?.type === 'ui:form:entityPreviewCard' ||
    schema?.type === 'ui:entityPreviewCard'
  )
}

export function isSubmitSearchValid(searchFilter: string, minQueryLength?: number): boolean {
  const trimmed = _.trim(searchFilter)
  const min = Math.max(1, Number(minQueryLength) || 0)
  return !_.isEmpty(trimmed) && trimmed.length >= min
}

/**
 * Label for the "swap the current selection" action rendered under a selected
 * card on NM and MW. Those surfaces have no room for a clear affordance beside
 * the card, and clearing was never the point — the user wants a different
 * entity, which is one action, not two.
 */
export function getFindDifferentLabel(entityDisplayName: string): string {
  return `Find a different ${entityDisplayName || DEFAULT_ENTITY_TYPE_NAME}`
}
